import AsyncSelect, { Option } from "./AsyncSelect";
import { useTranslation } from "react-i18next";
import { ComponentProps, forwardRef, useCallback } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { ReadClient } from "@/types/client";
import appConfiguration from "./cfg/AppCfg";
import { EntityListLink } from "./EntityListLink";
import { PATH_CLIENTS } from "@/constants";
import { ClientService } from "@/modules/client/service";

interface ClientSelectProps extends ControllerRenderProps {
  className?: string;
  defaultItem?: Partial<Option>;
  showCode?: boolean;
}

export default forwardRef<HTMLButtonElement, ClientSelectProps>(
  function ClientSelect(
    { value, defaultItem, showCode = true, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const options = useCreateDataTableOptions("clientList");
    const queryKey = useDynamicQueryParams<ReadClient>("clientList", options);

    const renderClientName = (item: ReadClient) => (
      <span className="flex items-center gap-1">
        <span>
          {item.name}
          {showCode && item.code && ` (${item.code})`}
        </span>
      </span>
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            ClientService.getList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("clientSelect.placeholder")}
          loadingLabel={t("clientSelect.loading")}
          noResultsLabel={t("clientSelect.noResults")}
          searchLabel={t("clientSelect.search")}
          enableFilter
          renderName={renderClientName}
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          defaultItem={defaultItem}
          ref={ref}
        />
        <EntityListLink path={PATH_CLIENTS} label={t("clientSelect.link")} />
      </span>
    );
  },
);
